import { useEffect, useState, useRef } from "react";
import { FileUp, FileSpreadsheet, FileText, FileType2, FileCheck2, Upload, CheckCircle2, AlertCircle } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { toast } from "sonner";

const FORMAT_ICONS = {
  csv: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  docx: FileType2,
  pdf: FileText,
  txt: FileCheck2,
};

export default function AdminImport() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [cats, setCats] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [topicId, setTopicId] = useState("");
  const [blockId, setBlockId] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  const cat = cats.find((c) => c.id === categoryId);

  const onFile = async (f) => {
    setFile(f);
    setPreview(null);
    setErr("");
    if (!f) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/import/preview", fd);
      setPreview(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    if (!file || !categoryId) {
      toast.error("Selecciona archivo y categoría");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category_id", categoryId);
      if (topicId) fd.append("topic_id", topicId);
      if (blockId) fd.append("block_id", blockId);
      const { data } = await api.post("/admin/import", fd);
      toast.success(`${data.inserted} preguntas importadas (${data.skipped} omitidas)`);
      setFile(null);
      setPreview(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const ext = file?.name?.split(".").pop()?.toLowerCase();
  const FormatIcon = FORMAT_ICONS[ext] || FileUp;

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-gc-gold mb-2">Importar</div>
      <h1 className="font-display text-3xl font-bold text-slate-900">Carga masiva de preguntas</h1>
      <p className="mt-2 text-sm text-slate-600">
        Sube tu archivo con preguntas. Soportamos CSV, Excel (.xlsx), Word (.docx) y PDF (.pdf).
      </p>

      {/* Format examples */}
      <details className="mt-6 bg-slate-50 border border-slate-200 rounded-sm">
        <summary className="cursor-pointer px-5 py-3 text-sm font-semibold text-slate-700">📋 Formatos aceptados — Ver ejemplo</summary>
        <div className="p-5 border-t border-slate-200 space-y-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-gc-gold mb-2">CSV / Excel</div>
            <pre className="bg-white border border-slate-200 rounded-sm p-3 text-[11px] font-mono overflow-auto text-slate-700">{`category_id,topic_id,block_id,question,opt_a,opt_b,opt_c,opt_d,correct,explanation
temario,constitucion,bloque-1,"¿Año de la CE?",1975,1976,1978,1981,c,"Aprobada en 1978"`}</pre>
            <p className="text-xs text-slate-500 mt-1.5">Columnas opcionales: <code>category_id</code>, <code>topic_id</code>, <code>block_id</code> (se sobrescriben con la selección de abajo). <code>correct</code> acepta a/b/c/d o 0/1/2/3.</p>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-gc-gold mb-2">Word / PDF / TXT — formato libre</div>
            <pre className="bg-white border border-slate-200 rounded-sm p-3 text-[11px] font-mono overflow-auto text-slate-700">{`1. ¿En qué año fue aprobada la Constitución Española?
a) 1975
b) 1976
c) 1978
d) 1981
Solución: c
Explicación: La CE se aprobó el 31 de octubre de 1978.

2. ¿Quién es el Jefe del Estado?
a) Presidente del Gobierno
b) Rey
c) Presidente del Congreso
d) Defensor del Pueblo
Solución: b`}</pre>
            <p className="text-xs text-slate-500 mt-1.5">Acepta variaciones: <code>1)</code> o <code>1.</code>, <code>a)</code> o <code>A.</code>, palabras clave <code>Solución</code>, <code>Respuesta</code>, <code>Correcta</code>, <code>Explicación</code>.</p>
          </div>
        </div>
      </details>

      {err && <Alert variant="destructive" className="mt-6"><AlertDescription>{err}</AlertDescription></Alert>}

      {/* Upload zone */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) onFile(f);
            }}
            className="border-2 border-dashed border-slate-300 hover:border-gc-green rounded-sm p-10 text-center cursor-pointer transition-colors bg-white"
            data-testid="upload-zone"
          >
            {file ? (
              <div>
                <FormatIcon className="h-10 w-10 mx-auto text-gc-green" />
                <div className="mt-3 font-semibold text-slate-900">{file.name}</div>
                <div className="text-xs text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB</div>
                <div className="text-xs text-gc-green mt-2 font-semibold">Click para cambiar archivo</div>
              </div>
            ) : (
              <div>
                <Upload className="h-10 w-10 mx-auto text-slate-400" />
                <div className="mt-3 font-medium text-slate-900">Arrastra un archivo aquí o haz clic</div>
                <div className="text-xs text-slate-500 mt-1">CSV · Excel · Word · PDF · TXT (máx. 10 MB)</div>
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xlsm,.docx,.pdf,.txt,.md"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
              data-testid="file-input"
            />
          </div>

          {/* Destination */}
          <div className="mt-6 space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Categoría destino *</Label>
              <Select value={categoryId} onValueChange={(v) => { setCategoryId(v); setTopicId(""); setBlockId(""); }}>
                <SelectTrigger className="mt-1.5 h-10" data-testid="import-category"><SelectValue placeholder="Selecciona categoría" /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Tema (opcional)</Label>
                <Select value={topicId || "__none__"} onValueChange={(v) => setTopicId(v === "__none__" ? "" : v)} disabled={!cat}>
                  <SelectTrigger className="mt-1.5 h-10"><SelectValue placeholder="(ninguno)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">(ninguno)</SelectItem>
                    {(cat?.topics || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Bloque (opcional)</Label>
                <Select value={blockId || "__none__"} onValueChange={(v) => setBlockId(v === "__none__" ? "" : v)} disabled={!cat?.blocks?.length}>
                  <SelectTrigger className="mt-1.5 h-10"><SelectValue placeholder="(ninguno)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">(ninguno)</SelectItem>
                    {(cat?.blocks || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-slate-500">El tema/bloque se asignará a las preguntas que no lo especifiquen en el archivo.</p>
          </div>
        </div>

        {/* Preview */}
        <div>
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-3 flex items-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5" /> Vista previa
            </div>
            {loading && <div className="text-sm text-slate-500">Analizando archivo...</div>}
            {!loading && !preview && (
              <div className="text-sm text-slate-400 text-center py-8">Sube un archivo para ver la previsualización.</div>
            )}
            {preview && (
              <div data-testid="import-preview">
                <div className="flex items-baseline justify-between mb-4">
                  <div className="font-display text-3xl font-bold text-gc-green">{preview.total}</div>
                  <div className="text-xs text-slate-500">preguntas detectadas</div>
                </div>
                {preview.total === 0 && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-sm">
                    <AlertCircle className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-800">No se detectaron preguntas. Comprueba el formato del archivo.</div>
                  </div>
                )}
                <div className="space-y-2 max-h-72 overflow-auto pr-1">
                  {preview.items.slice(0, 8).map((q, idx) => (
                    <div key={idx} className="text-xs border border-slate-100 bg-slate-50 rounded-sm p-2.5">
                      <div className="font-medium text-slate-900 line-clamp-2 mb-1">{idx + 1}. {q.question}</div>
                      <div className="text-[10px] text-emerald-700">
                        ✓ {String.fromCharCode(65 + q.correct_index)}: {q.options[q.correct_index]}
                      </div>
                    </div>
                  ))}
                  {preview.total > 8 && (
                    <div className="text-center text-xs text-slate-400 py-2">+{preview.total - 8} más...</div>
                  )}
                </div>
              </div>
            )}
          </div>

          <Button
            onClick={commit}
            disabled={!preview || !preview.total || !categoryId || loading}
            className="mt-4 w-full bg-gc-gold hover:bg-gc-gold-light text-white font-semibold h-11"
            data-testid="commit-import-button"
          >
            <Upload className="h-4 w-4 mr-2" />
            {loading ? "Importando..." : `Importar ${preview?.total || 0} preguntas`}
          </Button>
        </div>
      </div>
    </div>
  );
}
